import type { CanonicalFiles, ImportResult, LintDiagnostic } from '../../core/types.js';
import type { TargetCapabilities } from './target.interface.js';
import type { TargetLintHooks } from './target-descriptor.js';
import {
  BUILTIN_TARGETS,
  TARGET_IDS,
  isBuiltinTargetId,
  type BuiltinTargetId,
} from './builtin-targets.js';

type RuleLinter = (
  canonical: CanonicalFiles,
  projectRoot: string,
  projectFiles: string[],
  options?: { scope?: 'project' | 'global' },
) => LintDiagnostic[];

interface TargetCatalogEntry {
  importFrom: (root: string, options?: { scope?: 'project' | 'global' }) => Promise<ImportResult[]>;
  emptyImportMessage: string;
  lintRules: RuleLinter | null;
  lint?: TargetLintHooks;
  capabilities: TargetCapabilities;
}

export { TARGET_IDS, isBuiltinTargetId, type BuiltinTargetId };

export const TARGET_CATALOG: Record<BuiltinTargetId, TargetCatalogEntry> = Object.fromEntries(
  BUILTIN_TARGETS.map((target) => [
    target.id,
    {
      importFrom: target.generators.importFrom,
      emptyImportMessage: target.emptyImportMessage,
      lintRules: target.lintRules,
      lint: target.lint,
      capabilities: target.capabilities,
    },
  ]),
) as Record<BuiltinTargetId, TargetCatalogEntry>;

/**
 * Look up a builtin target by id. `BuiltinTargetId` is a closed union built
 * from `BUILTIN_TARGETS`, so every value indexes a real entry — no runtime
 * miss is reachable without a type-system escape hatch.
 */
export function getTargetCatalogEntry(id: BuiltinTargetId): TargetCatalogEntry {
  return TARGET_CATALOG[id];
}
